import type { FC, ReactNode } from 'react'
import type { NavLink } from '@/types'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { scrollToTarget } from '@/lib/motion/engine'
import { DrawLogo } from '@/components/generals/draw-logo'
import { OpenStatus } from '@/components/generals/open-status'
import { CONTACT, LINKS } from '@/constants/site'
import { COMPANY, RESOURCES, SOCIAL, TOOLS } from '@/constants/navigation'

interface FooterColumnProps {
    title: string
    links: NavLink[]
}

const FooterColumn: FC<FooterColumnProps> = ({ title, links }): ReactNode => (
    <div>
        <h2 className='t-label text-tone-muted'>{title}</h2>

        <ul className='mt-5 flex flex-col gap-2.5'>
            {links.map(link => (
                <li key={`${title}-${link.href}`}>
                    {link.external
                        ? (
                            <a href={link.href} target='_blank' rel='noopener noreferrer' className='u-link inline-flex items-center gap-1'>
                                {link.label}
                                <ArrowUpRight className='size-3.5 opacity-60' aria-hidden='true' />
                                <span className='sr-only'> (åpnes i ny fane)</span>
                            </a>
                        )
                        : (
                            <Link href={link.href} className='u-link'>
                                {link.label}
                            </Link>
                        )}
                </li>
            ))}
        </ul>
    </div>
)

export const Footer: FC = (): ReactNode => {
    const year = new Date().getFullYear()

    return (
        <footer className='tone-deep grain relative overflow-hidden' data-header='dark'>
            <div className='grid-lines' />

            <div className='wrap relative pt-24 pb-8 md:pt-32'>
                <div className='grid grid-cols-12 gap-x-6 gap-y-16'>
                    <div className='col-span-12 lg:col-span-5'>
                        <p className='t-label text-tone-muted'>Svenn · Programvare for håndverkere siden 2013</p>

                        <p className='t-h2 mt-6 max-w-[14ch] balance'>
                            Bygg overskudd <span className='t-serif text-signal'>fra første spiker.</span>
                        </p>

                        <OpenStatus className='mt-10 text-tone-muted' />

                        <div className='mt-6 flex flex-col gap-1'>
                            <a href={CONTACT.phoneHref} className='u-link t-h3 w-fit tabular'>{CONTACT.phone}</a>
                            <a href={`mailto:${CONTACT.email}`} className='u-link t-h3 w-fit'>{CONTACT.email}</a>
                            <a href={CONTACT.mapsHref} target='_blank' rel='noopener noreferrer' className='u-link mt-3 w-fit text-tone-muted'>
                                {CONTACT.address}
                                <span className='sr-only'> (åpnes i ny fane)</span>
                            </a>
                        </div>
                    </div>

                    <nav aria-label='Bunnmeny' className='col-span-12 grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-4 lg:col-span-7'>
                        <FooterColumn title='Verktøy' links={TOOLS.map(tool => ({ label: tool.name, href: tool.href }))} />
                        <FooterColumn title='Selskapet' links={COMPANY} />
                        <FooterColumn title='Ressurser' links={RESOURCES} />
                        <FooterColumn title='Følg oss' links={SOCIAL} />
                    </nav>
                </div>

                <DrawLogo className='mt-24 w-full text-paper md:mt-32' title='Svenn' />

                <div className='mt-10 flex flex-col gap-4 border-t border-tone-line pt-6 text-sm text-tone-muted md:flex-row md:items-center md:justify-between'>
                    <p>
                        <span suppressHydrationWarning>© {year}</span> Svenn. Laget for håndverkere i hele Norge.
                    </p>

                    <div className='flex flex-wrap items-center gap-x-6 gap-y-2'>
                        <a href={LINKS.privacy} target='_blank' rel='noopener noreferrer' className='u-link'>
                            Personvern og vilkår
                            <span className='sr-only'> (åpnes i ny fane)</span>
                        </a>
                        <a href={LINKS.help} target='_blank' rel='noopener noreferrer' className='u-link'>
                            Hjelpesenter
                            <span className='sr-only'> (åpnes i ny fane)</span>
                        </a>
                        <button
                            type='button'
                            onClick={() => scrollToTarget(0, 0)}
                            className='inline-flex items-center gap-2 text-tone-fg'
                        >
                            Til toppen
                            <ArrowUp className='size-4' aria-hidden='true' />
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    )
}
